/** A canvas element as react-flow's selection sees it: nodes and edges both carry an optional `selected` flag. */
interface Selectable {
  readonly id: string;
  readonly selected?: boolean;
}

/** What the property panel shows: the one selected node or the one selected edge; otherwise an empty panel. */
export type FlowPanelTarget =
  | { readonly kind: 'node'; readonly id: string }
  | { readonly kind: 'edge'; readonly id: string }
  | { readonly kind: 'multiple'; readonly count: number }
  | { readonly kind: 'none' };

/**
 * The panel follows react-flow's selection, so that what is outlined, what the panel shows and what Backspace deletes are
 * always the same element. A multi-selection has no single element to show.
 */
export const panelTarget = (nodes: readonly Selectable[], edges: readonly Selectable[]): FlowPanelTarget => {
  const [node, ...moreNodes] = nodes.filter((n) => n.selected);
  const [edge, ...moreEdges] = edges.filter((e) => e.selected);
  if (node && moreNodes.length === 0 && !edge) return { kind: 'node', id: node.id };
  if (edge && moreEdges.length === 0 && !node) return { kind: 'edge', id: edge.id };
  const count = (node ? 1 + moreNodes.length : 0) + (edge ? 1 + moreEdges.length : 0);
  return count === 0 ? { kind: 'none' } : { kind: 'multiple', count };
};

/** The property panel's own state: the user's choice, which a selection change never overrides once the panel is open. */
export type FlowPanelMode = 'closed' | 'collapsed' | 'expanded';

/**
 * A closed panel opens, expanded, when the selection gives it one element to show. An open panel keeps its mode whatever
 * the selection becomes: a collapsed one stays collapsed, and a deselect or multi-select leaves it open on an empty state.
 */
export const panelModeOnSelection = (mode: FlowPanelMode, target: FlowPanelTarget): FlowPanelMode =>
  mode === 'closed' && (target.kind === 'node' || target.kind === 'edge') ? 'expanded' : mode;
